import { jsx } from '@emotion/react';
import { FullStoryAPI } from '@zapier/react-fullstory';
import { formatTimeSpan } from '@zapier/date';
import { format } from 'date-fns';
import { Icon, Link, Typography } from '@zapier/design-system';
import { Tooltip } from '@zapier/design-system-beta';
import { labelStyles } from '@zapier/style-encapsulation';
import { emitUserInteractionClick, InteractionGoal, InteractionName, } from '../../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../../hooks';
import UsageBar from './UsageBar';
import { baseReset, boxSizing } from '../../../../../utils/style-mixins/reset';
const Styles = labelStyles('UniversalSidebarUsage', {
    root: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gap: 'var(--zds-space-12, 12px)', whiteSpace: 'nowrap' }),
    header: Object.assign(Object.assign({}, boxSizing), { font: Typography.base.fontFamily, fontSize: 16, fontWeight: 600, color: 'var(--zds-text-default, #413735)' }),
    planName: Object.assign(Object.assign({}, baseReset), { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', font: `var(--zds-body-2)` }),
    usages: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gap: 'var(--zds-space-16, 16px)' }),
    infoContainer: (hasTrial) => (Object.assign(Object.assign({}, baseReset), { display: 'grid', font: `var(--zds-minimal-print-1)`, color: 'var(--zds-text-weakest, #6F6765)', gap: hasTrial ? 'var(--zds-space-4, 4px)' : 'var(--zds-space-28, 28px)', paddingTop: hasTrial
            ? 'var(--zds-space-28, 28px)'
            : 'var(--zds-space-16, 16px)', '> a': {
            color: 'var(--zds-text-link, #695BE8) !important',
            width: 'max-content',
            '&:hover': {
                color: 'var(--zds-text-link-hover, #503ebd) !important',
            },
            '&:focus-visible': {
                outlineColor: 'var(--zds-text-link, #695BE8) !important',
            },
            '&:focus': {
                outlineColor: 'none !important',
            },
        } })),
    usageBarPPTLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--zds-space-4, 4px)',
    },
    tooltipIconContainer: {
        display: 'flex',
        alignSelf: 'center',
    },
});
const PAY_PER_TASK_BILLING_LABEL = "Pay-per-task billing for tasks above your plan's limit";
const UsageBarPPTLabel = (jsx("span", { css: Styles.usageBarPPTLabel },
    "Pay-per-task billing",
    jsx(Tooltip, { allowMultiline: true, content: PAY_PER_TASK_BILLING_LABEL },
        jsx(Tooltip.FocusableTrigger, null,
            jsx("span", { css: Styles.tooltipIconContainer, role: "img", "aria-label": PAY_PER_TASK_BILLING_LABEL },
                jsx(Icon, { "aria-hidden": false, name: "alertInfo", color: "GrayWarm6", size: 16 }))))));
const Usages = ({ isPlanMetered, numTasksUsed, effectiveTaskLimit, numTasksMax, numZaps, numZapsMax, isWorkspace, isPayPerTaskEnabled, }) => {
    const isLimitSet = effectiveTaskLimit !== null;
    const isInfiniteEffectiveTaskLimit = effectiveTaskLimit === Infinity;
    const hasUsedExtraTasks = isLimitSet && isPlanMetered && numTasksUsed > effectiveTaskLimit;
    const usageBarLabel = isWorkspace ? 'Tasks used' : 'Plan tasks';
    return (jsx("div", { css: Styles.usages, "data-zds": true },
        jsx(UsageBar, { label: usageBarLabel, max: effectiveTaskLimit !== null && effectiveTaskLimit !== void 0 ? effectiveTaskLimit : undefined, value: hasUsedExtraTasks ? effectiveTaskLimit : numTasksUsed }),
        isLimitSet &&
            !isWorkspace &&
            !isInfiniteEffectiveTaskLimit &&
            (hasUsedExtraTasks || (isPayPerTaskEnabled && isPlanMetered)) && (jsx(UsageBar, { label: UsageBarPPTLabel, max: numTasksMax === Infinity
                ? Infinity
                : numTasksMax - effectiveTaskLimit, value: hasUsedExtraTasks ? numTasksUsed - effectiveTaskLimit : 0 })),
        !isWorkspace && numZapsMax !== Infinity && (jsx(UsageBar, { label: "Zaps", max: numZapsMax, value: numZaps }))));
};
const PlanUsage = ({ effectiveTaskLimit, numTasksUsed, numTasksMax, numZaps, numZapsMax, resetDate, children = undefined, trialPlanName = undefined, trialPlanEndDate = undefined, isPlanMetered = false, managePlanHref = '/app/settings/billing', managePlanLabel = 'Manage plan', title, isWorkspace = false, isPayPerTaskEnabled = false, isAccountAdmin = false, }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const managePlanURL = getFullyQualifiedURL(managePlanHref);
    const relativeDate = formatTimeSpan(Date.now(), resetDate, false);
    const handleEmitAvroClickEvent = (event) => {
        event.preventDefault();
        FullStoryAPI('event', `Plan usage click from UniversalLayout sidebar`);
        emitUserInteractionClick(InteractionName.plan_usage_click, InteractionGoal.plan_usage, managePlanLabel, managePlanHref).finally(() => (location.href = managePlanURL || ''));
    };
    const hasTrial = trialPlanName && trialPlanEndDate;
    const trialPlanNameShort = trialPlanName === 'Professional' ? 'Pro' : trialPlanName;
    return (jsx("section", { css: Styles.root, "data-zds": true },
        title && (jsx("div", { css: Styles.header, "data-zds": true },
            jsx("h3", { css: Styles.planName, "data-zds": true }, title))),
        jsx("div", { "data-zds": true },
            jsx("div", null,
                jsx(Usages, { effectiveTaskLimit,
                    isPlanMetered,
                    numTasksUsed,
                    numTasksMax,
                    numZaps,
                    numZapsMax,
                    isWorkspace,
                    isPayPerTaskEnabled })),
            jsx("p", { css: Styles.infoContainer(hasTrial), "data-zds": true },
                jsx("span", { "data-zds": true }, hasTrial
                    ? `Zaps ${trialPlanNameShort} trial ends ${format(new Date(trialPlanEndDate), 'MMM D')}`
                    : `Usage resets in ${relativeDate}`),
                !isWorkspace && isAccountAdmin && (jsx(Link, { onClick: handleEmitAvroClickEvent, href: managePlanURL }, managePlanLabel)))),
        children));
};
export default PlanUsage;
//# sourceMappingURL=PlanUsage.js.map
// @zapier/universal-layout@12.2.9 (038caa1ab)
